<script setup lang="ts">
import { computed } from "vue";
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import { PageBlock } from "@/views/blocks/PageBlock.interface";
import EditableElement from "@/views/page/EditableElement.vue";

// Import shared components
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Image from "@/views/blocks/shared/section/image/Image.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";

import type { FullWidthWithVerticalImagesProps } from "./FullWidthWithVerticalImagesProps";

const props = defineProps<{
    block: PageBlock<FullWidthWithVerticalImagesProps>;
    pageColors: CssVarColors;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <Section :id="block.webId" :backgroundColor="styleValues.background">
        <FullSpacing>
            <div class="max-w-2xl lg:max-w-7xl mx-auto">
                <div class="max-w-2xl mx-auto sm:text-center">
                    <EditableElement
                        v-if="!dataValues.title.isHidden"
                        :blockId="block.id"
                        field-name="title"
                    >
                        <Heading
                            :text="dataValues.title.data.text"
                            :tag="dataValues.title.data.tag"
                            :textColor="styleValues.text"
                            class="text-4xl lg:text-5xl mb-6"
                        />
                    </EditableElement>
                    <EditableElement
                        v-if="!dataValues.description.isHidden"
                        :blockId="block.id"
                        field-name="description"
                    >
                        <Paragraph
                            :text="dataValues.description.data.text"
                            :textColor="styleValues.text"
                            class="text-lg/8"
                        />
                    </EditableElement>
                </div>
                <div
                    class="grid grid-cols-1 sm:grid-cols-2 max-w-4xl xl:max-w-none mx-auto gap-x-8 gap-y-20 mt-20"
                >
                    <EditableElement
                        v-for="member in dataValues.teamMembers.data.items"
                        :key="member.id"
                        :blockId="block.id"
                        :field-name="`teamMembers_${member.id}`"
                        :hideable="false"
                    >
                        <div class="flex flex-col xl:flex-row gap-6">
                            <Image
                                :src="member.imageSrc"
                                :alt="member.name"
                                class="aspect-[4/5] max-w-full w-52 rounded-2xl object-cover"
                                width="208"
                                height="260"
                            />
                            <div class="max-w-xl">
                                <Paragraph
                                    :text="member.name"
                                    :textColor="styleValues.text"
                                    class="text-lg/8 font-semibold"
                                />
                                <Paragraph
                                    :text="member.role"
                                    :textColor="styleValues.text"
                                    class="text-base/7 opacity-70"
                                />
                                <Paragraph
                                    :text="member.description"
                                    :textColor="styleValues.text"
                                    class="text-base/7 mt-6 opacity-70"
                                />
                            </div>
                        </div>
                    </EditableElement>
                </div>
            </div>
        </FullSpacing>
    </Section>
</template>

<style scoped></style>
